import React, { FC, FormEvent, Fragment, useEffect } from 'react';
import ImagePreview from '../image-preview/image-preview';
import PasswordInput from '../password-input/password-input';
import styles from './form.module.css';

const Form: FC<{
  formData: FormParams;
  validations: object;
  handleOnChange: (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => void;
  previewImage?: string;
  isLoadingImage?: boolean;
  forceRemovePreviewImage?: (id: string) => void;
  title?: string;
  attachments?: { id: number | string; originalName: string }[];
  isLoadingAttachment?: boolean;
  deleteAttachment?: (id: number | string) => void;
  onDropFilePreview?: any;
  onDeleteFilePreview?: (id: number) => void;
  fieldHints?: { [fieldId: string]: React.ReactNode };
}> = ({
  formData,
  validations,
  handleOnChange,
  previewImage,
  isLoadingImage,
  forceRemovePreviewImage,
  title,
  attachments,
  isLoadingAttachment,
  deleteAttachment,
  onDropFilePreview,
  onDeleteFilePreview,
  fieldHints
}) => {
    const submitData = (e: FormEvent) => {
      e.preventDefault();

      if (formData.submitCallback) formData.submitCallback();
    };

    useEffect(() => {
      const previewElement = document.getElementById(
        'image-preview'
      ) as HTMLImageElement;

      if (typeof previewImage === 'string') {
        previewElement.src = previewImage;
      }
    }, [previewImage]);

    const renderItem = (
      item:
        | InputData
        | TextareaData
        | SideElements
        | SelectElement
        | TitleElement
        | SubtitleElement
        | ResponsibleElement
        | DragDropPreview,
      key: number
    ) => {
      const getValidFilesTypes = () => {
        if (item.element === 'input' && item.type === 'file') {
          if (item.id === 'image') return '.jpeg, .jpg, .png';
          else if (item.id === 'attachment')
            return '.pdf, .doc, .docx, .jpeg, .jpg, .png';
          else return undefined;
        }

        return undefined;
      };

      if (item.element === 'input') {
        const inputWithError = validations[item.id] ? true : false;

        return (
          <div
            className={styles.inputContainer}
            style={item.containerStyle}
            key={key}
          >
            {item.type === 'file' &&
              item.id === 'attachment' &&
              attachments &&
              attachments.length > 0 && (
                <div className={styles.attachmentContainer}>
                  {attachments.map((attachment, index) => (
                    <div className={styles.attachmentItem} key={index}>
                      <p>{attachment.originalName}</p>
                      <p
                        className={styles.attachmentDelete}
                        onClick={
                          deleteAttachment
                            ? () => deleteAttachment(attachment.id)
                            : null
                        }
                      >
                        x
                      </p>
                    </div>
                  ))}
                </div>
              )}
            {item.type === 'file' &&
              item.id === 'image' &&
              previewImage &&
              !isLoadingImage ? null : (
              <Fragment>
                {item.label && <label htmlFor={item.id}>{item.label}</label>}
                {item.type === 'password' ? (
                  <PasswordInput
                    id={item.id}
                    defaultValue={item.defaultValue}
                    placeholder={item.placeHolder}
                    onChange={handleOnChange}
                    onBlur={handleOnChange}
                    className={inputWithError ? styles.inputError : undefined}
                  />
                ) : (
                <input
                  id={item.id}
                  type={item.type}
                  defaultValue={item.defaultValue}
                  placeholder={item.placeHolder}
                  onChange={handleOnChange}
                  onBlur={handleOnChange}
                  min={item.minDate ? item.minDate : undefined}
                  max={item.maxDate ? item.maxDate : '2999-12-31'}
                  className={inputWithError ? styles.inputError : undefined}
                  accept={getValidFilesTypes()}
                  multiple={
                    item.type === 'file' && item.id === 'attachment'
                      ? true
                      : undefined
                  }
                  autoCapitalize={item.type === 'email' || item.id.toLowerCase().includes('email') ? 'none' : undefined}
                  autoCorrect={item.type === 'email' || item.id.toLowerCase().includes('email') ? 'off' : undefined}
                />
                )}
              </Fragment>
            )}
            <div
              className={
                item.type === 'file' &&
                  item.id === 'image' &&
                  previewImage &&
                  !isLoadingImage
                  ? styles.imagePreviewContainerShow
                  : styles.imagePreviewContainerHide
              }
            >
              <div
                id='image-preview'
                className={styles.previewImage}
                style={{
                  backgroundImage: `url(${previewImage ? previewImage : ''})`,
                }}
              />
              <p onClick={() => forceRemovePreviewImage(item.id)}>
                remover imagem
              </p>
            </div>
            {item.type === 'file' &&
              item.id === 'image' &&
              !previewImage &&
              isLoadingImage && <p>carregando imagem...</p>}
            {item.type === 'file' &&
              item.id === 'attachment' &&
              isLoadingAttachment && <p>carregando arquivos...</p>}
            {fieldHints && fieldHints[item.id]}
            {inputWithError && (
              <p className={styles.validationMessage}>{validations[item.id]}</p>
            )}
          </div>
        );
      } else if (item.element === 'textarea') {
        const inputWithError = validations[item.id] ? true : false;

        return (
          <div
            className={styles.textareaContainer}
            style={item.containerStyle}
            key={key}
          >
            {item.label && <label htmlFor={item.id}>{item.label}</label>}
            <textarea
              id={item.id}
              defaultValue={item.defaultValue}
              placeholder={item.placeHolder}
              rows={item.rows}
              onChange={handleOnChange}
              onBlur={handleOnChange}
              className={inputWithError ? styles.inputError : undefined}
            />
            {inputWithError && (
              <p className={styles.validationMessage}>{validations[item.id]}</p>
            )}
          </div>
        );
      } else if (item.element === 'side') {
        return (
          <div className={styles.sideContainer} key={key}>
            <div className={styles.leftSideContainer}>
              {renderItem(item.leftElement, key)}
            </div>
            <div className={styles.rightSideContainer}>
              {renderItem(item.rightElement, key)}
            </div>
          </div>
        );
      } else if (item.element === 'select') {
        const inputWithError = validations[item.id] ? true : false;

        return (
          <div className={styles.selectContainer} key={key}>
            {item.label && <label htmlFor={item.id}>{item.label}</label>}
            <select
              id={item.id}
              className={inputWithError ? styles.inputError : undefined}
              onChange={handleOnChange}
              onBlur={handleOnChange}
              defaultValue={item.defaultValue || 'default'}
            >
              <option value='default' disabled hidden>
                Selecionar
              </option>
              {item.options.map((option, index) => (
                <option value={option.value} key={index}>
                  {option.text}
                </option>
              ))}
            </select>
            {inputWithError && (
              <p className={styles.validationMessage}>{validations[item.id]}</p>
            )}
          </div>
        );
      } else if (item.element === 'title') {
        return (
          <h3 className={styles.titleElement} key={key}>
            {item.value}
          </h3>
        );
      } else if (item.element === 'subtitle') {
        return (
          <h3 className={styles.subtitleElement} key={key}>
            {item.value}
          </h3>
        );
      } else if (item.element === 'responsible') {
        return (
          <div key={key}>
            {item.inputs.map((input, index) => {
              return (
                <div key={index}>
                  {renderItem(input.subtitle, index + 1)}
                  {renderItem(input.name, index + 2)}
                  {renderItem(input.phone, index + 3)}
                  {renderItem(input.email, index + 4)}
                </div>
              );
            })}
          </div>
        );
      } else if (item.element === 'drag-drop-preview') {
        const inputWithError = validations[item.id] ? true : false;

        return (
          <div
            className={styles.imagePreviewContainer}
            style={item.containerStyle}
            key={key}
          >
            {item.label && <label htmlFor={item.id}>{item.label}</label>}
            <ImagePreview
              maxFiles={1}
              imageList={item.imageList}
              onDrop={onDropFilePreview}
              onDelete={onDeleteFilePreview}
              thumbnailClassName={styles.previewImage}
            />
            {inputWithError && (
              <p className={styles.validationMessage}>{validations[item.id]}</p>
            )}
          </div>
        );
      }
      else {
        return null;
      }
    };

    const renderUpperItems = () => {
      const items = formData.items.upperFields;
      let itens: any[] | null = null;

      if (items && items.length > 0) {
        itens = items.map((item, key) => renderItem(item, key));
      }

      return itens;
    };

    const renderBottomItems = () => {
      const items = formData.items.bottomFields;
      let itens: any[] | null = null;

      if (items && items.length > 0) {
        itens = items.map((item, key) => renderItem(item, key));
      }

      return itens;
    };

    const getFormStyle = () => {
      switch (formData.style) {
        case 'gray':
          return styles.formGray;
        case 'white':
          return styles.formWhite;
        default:
          return styles.formWhite;
      }
    };

    return (
      <div className={getFormStyle()}>
        <form className={styles.formElement} onSubmit={submitData}>
          <div className={styles.upperFields}>
            {title && <h3 className={styles.titleElement}>{title}</h3>}
            {renderUpperItems()}
          </div>
          {formData.items.bottomFields && (
            <div className={styles.bottomFields}>{renderBottomItems()}</div>
          )}
        </form>
      </div>
    );
  };

export default Form;
